import React, {
  useState,
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react"
import PropTypes from "prop-types"
import { isMobile } from "react-device-detect"
import Button from "@/components/core/Button/Button"
import cx from "classnames"
import Styles from "@/components/core/StepNavigator/StepNav.module.scss"
import ShippingFiller from "@/components/checkout/ShippingFiller"
import {
  setIsPaymentInfoAction,
  setCheckoutAction,
} from "@/store/features/checkoutSlice"
import { useDispatch } from "react-redux"
import {
  editCartAnalytics,
  editDeliveryAnalytics,
} from "@/components/checkout/v1/checkoutAnalytics"
import { editButtonAnalyticsEventInfoStep } from "@/components/RegisterOfflineProduct/v1/RegisterOfflineHelper"

const StepNavigator = forwardRef(
  (
    {
      data,
      placeOrder,
      handleSuccess,
      handlePaymentStep = () => {},
      checkoutMode = "",
      addrSet = false,
      changeCurStep = () => {},
    },
    ref
  ) => {
    const [curStep, setCurStep] = useState(
      checkoutMode === "one-step" && addrSet === "true" ? 1 : 0
    )
    const statusCode = {
      PENDING: "pending",
      COMPLETE: "complete",
      EDIT: "edit",
    }
    const [status, setStatus] = useState(
      data?.map((d, i) => {
        if (i === 0 && checkoutMode === "one-step" && addrSet === "true") {
          return statusCode.COMPLETE
        }
        return statusCode.PENDING
      })
    )
    const [open, setOpen] = useState(data?.map(() => false))
    const stepNavRef = useRef([])
    const dispatch = useDispatch()

    const handleNext = () => {
      updateStatus(curStep, statusCode.COMPLETE)
      if (curStep < data?.length - 1) {
        setCurStep(curStep + 1)
      }
    }
    useImperativeHandle(ref, () => ({ handleNext }))

    const handleEdit = step => {
      window.pageLoad = false

      if (step === 0) {
        if (data[0]?.label === "Personal Information") {
          editButtonAnalyticsEventInfoStep("personal information")
        } else {
          editCartAnalytics()
        }
      }
      if (step === 1) {
        if (data[1]?.label === "Product Information") {
          editButtonAnalyticsEventInfoStep("product information")
        } else {
          editDeliveryAnalytics()
        }
      }
      setCurStep(step)
    }

    useEffect(() => {
      document.addEventListener("set-step", changeStep)
      window.pageLoad = true
      return () => {
        document.removeEventListener("set-step", changeStep)
      }
    }, [])

    const changeStep = e => {
      if (curStep !== 0) {
        setCurStep(e?.detail?.count ?? 0)
      }
    }

    useEffect(() => {
      if (curStep !== 3) {
        dispatch(setIsPaymentInfoAction(false))
      }
      changeCurStep(curStep)
      handlePaymentStep(curStep)
      dispatch(setCheckoutAction(curStep))
    }, [curStep])

    const handleOpen = (step, label) => {
      const newOpen = [...open]
      newOpen[step] = !open[step]
      setOpen(newOpen)
      addAnalyticsData(step, newOpen, label)
    }

    const addAnalyticsData = (step, newOpen, label) => {
      const collapseExpand = newOpen[step] ? "expand" : "collapse"
      const type = label?.toLowerCase()
      const { adobeDataLayer: { getState } = {} } = window
      const page = (getState && getState("page")) || {}
      const onlineOrOffline =
        window.adobeDataLayer[1]?.page?.category?.subCategoryID?.includes(
          "offline"
        )
          ? "offline"
          : "online"
      const eventInfo = {
        clickInternalLinks: "true",
        eventAction: `my account:register ${onlineOrOffline} kohler products:${type}:accordion ${collapseExpand}`,
        eventName: `my account:register ${onlineOrOffline} kohler products:${type}:accordion ${collapseExpand}`,
        eventMsg: "n/a",
        eventStatus: "n/a",
        eventType: "navigation",
        internalLinkName: collapseExpand,
        internalLinkPosition: "register products",
        internalLinkType: "my account:navigation",
        internalLinkURL: "n/a",
        internalLinkZoneName: `my account:register ${onlineOrOffline} kohler products:${type}`,
      }
      window.adobeDataLayer.push({
        event: "cmp:click",
        eventInfo,
        page,
      })
    }

    const updateStatus = (step, code) => {
      const newStatus = [...status]
      newStatus[step] = code
      if (code === statusCode.EDIT) {
        if (step !== 0 && !window?.pageLoad) {
          stepNavRef.current[step]?.scrollIntoView(true)
        }
        for (let i = step + 1; i < data?.length; i++) {
          newStatus[i] = statusCode.PENDING
        }
      }
      setStatus(newStatus)
    }

    useEffect(() => updateStatus(curStep, statusCode.EDIT), [curStep])

    return (
      <div className={Styles.stepNavWrapper}>
        <div className="step-nav">
          {data?.map(
            (
              {
                label,
                editButtonAnalytics,
                content: Component,
                props,
                ref = null,
              },
              i
            ) => (
              <div
                className={cx({
                  "step-nav__step": true,
                  "step-nav__step--active": i === curStep,
                  "step-nav__step--done": status[i] === statusCode.COMPLETE,
                })}
                key={i}
                id={`step-nav-step-${i}`}
                tabIndex="-1"
                ref={element => stepNavRef.current.push(element)}
              >
                <div className="step-nav__number d-none d-lg-block">
                  {i + 1}
                </div>
                <div className="step-nav__content">
                  <div className="step-nav__title">
                    <div
                      className="step-nav__title-container"
                      aria-current={i === curStep ? "step" : ""}
                    >
                      <h2>
                        <span className="step-nav__number--mobile">
                          {i + 1}{" "}
                        </span>
                        {label}
                      </h2>
                    </div>
                    {curStep > i && !props.hideEdit && (
                      <Button
                        role="button"
                        ariaLabel={`Edit ${label}`}
                        customClass="step-nav__edit-btn"
                        className={editButtonAnalytics ? "gbh-data-layer" : ""}
                        size="small"
                        type="ghost"
                        label="Edit"
                        onClick={() => handleEdit(i)}
                      />
                    )}
                    {status[i] === statusCode.COMPLETE && (
                      <div
                        tabIndex="0"
                        role="button"
                        aria-label={`Edit ${label}`}
                        aria-expanded={open[i]}
                        className={`step-nav__plus ${open[i] ? "--open" : ""}`}
                        onClick={() => handleOpen(i, label)}
                      >
                        <div className="line line-1"></div>
                        <div className="line line-2"></div>
                      </div>
                    )}
                  </div>
                  {label === "Shipping" && status[i] === statusCode.PENDING && (
                    <div role="group" className="step-nav__component--open">
                      <ShippingFiller />
                    </div>
                  )}
                  <div
                    role="group"
                    className={`step-nav__component ${
                      status[i] === statusCode.EDIT ||
                      (status[i] === statusCode.COMPLETE && open[i])
                        ? "--open"
                        : ""
                    } ${
                      status[i] === statusCode.EDIT ||
                      (status[i] === statusCode.COMPLETE && open[i])
                        ? "order-summary-mobile-block"
                        : ""
                    }`}
                  >
                    {status[i] !== statusCode.PENDING && (
                      <Component
                        status={status[i]}
                        statusCode={statusCode}
                        {...props}
                        onNext={handleNext}
                        placeOrder={placeOrder}
                        handleSuccess={handleSuccess}
                        curStep={curStep}
                        ref={ref}
                      />
                    )}
                    {curStep > i && isMobile && (
                      <Button
                        role="button"
                        tabIndex={open[i] ? 0 : 1}
                        ariaLabel={`Edit ${label}`}
                        className={editButtonAnalytics ? "gbh-data-layer" : ""}
                        data-gbh-data-layer={editButtonAnalytics || null}
                        customClass="step-nav__edit-btn--mobile"
                        size="small"
                        type="ghost"
                        label="Edit"
                        onClick={() => handleEdit(i)}
                        useDefaultClass
                      />
                    )}
                  </div>
                </div>
              </div>
            )
          )}
        </div>
      </div>
    )
  }
)

export default StepNavigator

StepNavigator.propTypes = {
  data: PropTypes.array.isRequired,
}
